/**
 * Date/time utility functions for the EventModal.
 * Extracted to comply with file-size and single-responsibility standards.
 */

/**
 * Extracts the date portion (MM/DD/YYYY) from a zulu datetime string.
 * Accepts both space-separated ("YYYY-MM-DD HH:MM:SS") and ISO 8601
 * ("YYYY-MM-DDTHH:MM:SSZ") formats.
 */
export function extractDate(datetime: string): string {
  if (!datetime) return '';
  const datePart = datetime.split(/[ T]/)[0];
  if (!datePart) return '';
  const segments = datePart.split('-');
  if (segments.length === 3) {
    return `${segments[1]}/${segments[2]}/${segments[0]}`;
  }
  return datePart;
}

/**
 * Extracts the time portion (HH:MM) from a zulu datetime string.
 * Accepts both space-separated ("YYYY-MM-DD HH:MM:SS") and ISO 8601
 * ("YYYY-MM-DDTHH:MM:SSZ") formats.
 */
export function extractTime(datetime: string): string {
  if (!datetime) return '';
  const parts = datetime.split(/[ T]/);
  if (parts.length < 2) return '';
  return parts[1].substring(0, 5);
}

/**
 * Combines date (MM/DD/YYYY) and time (HH:MM) back into a standard datetime string.
 */
export function combineDateTime(date: string, time: string): string {
  if (!date && !time) return '';
  const dateParts = date.split('/');
  let isoDate = '';
  if (dateParts.length === 3) {
    isoDate = `${dateParts[2]}-${dateParts[0]}-${dateParts[1]}`;
  } else {
    isoDate = date;
  }
  return time ? `${isoDate} ${time}:00` : isoDate;
}

/**
 * Ensures a time string is displayed in HH:MM format.
 * If it's raw digits (e.g., "1235"), inserts the colon.
 */
export function formatTimeDisplay(time: string): string {
  if (!time) return '';
  if (time.includes(':')) return time;
  const digits = time.replace(/[^0-9]/g, '');
  if (digits.length >= 3) {
    return digits.substring(0, 2) + ':' + digits.substring(2, 4);
  }
  return digits;
}

/**
 * Formats a stored barrier datetime (e.g., "2026-05-07 1235") to display as "2026-05-07 12:35".
 */
export function formatBarrierDateTime(datetime: string): string {
  if (!datetime) return '';
  const parts = datetime.split(' ');
  if (parts.length < 2) return datetime;
  return `${parts[0]} ${formatTimeDisplay(parts[1])}`;
}

/**
 * Returns the ISO date portion (`YYYY-MM-DD`) of a stored zulu value, or '' when
 * absent or not a valid ISO date. Guards native `<input type="date">` elements
 * from receiving a garbage value (which breaks their formatting) when the stored
 * value holds only a time (e.g. the user typed the time before picking a date).
 */
export function isoDatePart(datetime: string): string {
  if (!datetime) return '';
  const part = datetime.split(/[ T]/)[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(part) ? part : '';
}

/**
 * Composes an ISO date (`YYYY-MM-DD` or '') and a partial/full time display
 * ('', 'H', 'HH', 'HH:M', or 'HH:MM') into the stored zulu value. A space
 * separator is kept whenever a time is present — even with no date yet — so both
 * fields round-trip cleanly while one part is still being entered. A complete
 * `HH:MM` gets `:00` seconds appended.
 */
export function composeZuluDateTime(isoDate: string, timeDisplay: string): string {
  const date = isoDate || '';
  const time = timeDisplay || '';
  if (!date && !time) return '';
  if (!time) return date;
  const timePart = time.length === 5 ? `${time}:00` : time;
  return `${date} ${timePart}`;
}

/**
 * True when a stored zulu value is missing a valid ISO date or a usable
 * (>= 4 char) time. Used to gate required-field highlighting and the save
 * button so a date-less or time-less entry is treated as incomplete.
 */
export function isDateTimeIncomplete(value: string | undefined): boolean {
  if (!value || !value.includes(' ')) return true;
  const [datePart, timePart] = value.split(' ');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return true;
  return (timePart?.length ?? 0) < 4;
}

/**
 * Extracts and formats the time portion from an ERTS datetime for display.
 * Handles partial input (raw digits) and full stored values (with seconds).
 */
export function getErtsTimeDisplay(datetime: string): string {
  if (!datetime) return '';
  const parts = datetime.split(' ');
  if (parts.length < 2) return '';
  const timePart = parts[1];
  if (timePart.includes(':')) {
    const segments = timePart.split(':');
    return `${segments[0]}:${segments[1]}`;
  }
  const digits = timePart.replace(/[^0-9]/g, '');
  if (digits.length >= 3) {
    return digits.substring(0, 2) + ':' + digits.substring(2, 4);
  }
  return digits;
}

/**
 * Converts an epoch-seconds instant into the `YYYY-MM-DD HH:MM:SS` (UTC/Zulu)
 * shape stored in event date-time fields (e.g. nextScheduledFlightTimeZulu).
 * Returns '' for missing or non-finite input.
 */
export function epochSecondsToZulu(epochSeconds: number | null | undefined): string {
  if (epochSeconds === null || epochSeconds === undefined || !Number.isFinite(epochSeconds)) {
    return '';
  }
  const iso = new Date(epochSeconds * 1000).toISOString(); // YYYY-MM-DDTHH:MM:SS.sssZ
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`;
}

/**
 * Parses a stored zulu value (`YYYY-MM-DD HH:MM[:SS]`, treated as UTC) into
 * epoch seconds. Returns undefined for missing or unparseable input.
 */
export function zuluToEpochSeconds(value: string | undefined | null): number | undefined {
  if (!value) return undefined;
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::(\d{2}))?/);
  if (!match) return undefined;
  const milliseconds = Date.parse(`${match[1]}T${match[2]}:${match[3] || '00'}Z`);
  return Number.isNaN(milliseconds) ? undefined : Math.floor(milliseconds / 1000);
}
